import { reducei18n } from '@atlas/dynamic-form-client';
import {
  Button,
  FormLayout,
  Heading,
  HorizontalRule,
  IconError,
  Link,
  Paragraph,
  Section,
} from '@piggybank/core';
import get from 'lodash/get';
import T from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { renderMarkdown } from 'srcAlias/global/components/Markdown';
import {
  Optional,
  Translation,
  TranslationScope,
} from 'srcAlias/global/locale';
import { useLogError } from 'srcAlias/utils/useLog';

export const TechnicalError = ({
  applicationId,
  applicationStatus,
  error,
  identityVerified,
  interactionHistory,
  productCodes,
  reasonCode,
  responseCode,
  sessionId,
  showStackTrace,
  title,
}) => {
  useLogError({
    message: get(error, 'message', ''),
    params: {
      category: get(error, 'category', 'unknown'),
      responseCode: responseCode || get(error, 'data.responseCode'),
      reasonCode: reasonCode || get(error, 'data.reasonCode'),
      applicationId: applicationId || get(error, 'data.applicationId', ''),
      applicationStatus:
        applicationStatus || get(error, 'data.applicationStatus', ''),
      statusCode: get(error, 'statusCode'),
      segment: get(interactionHistory, 'currentSegmentName', ''),
      page: get(interactionHistory, 'currentPageName', ''),
      trace: get(interactionHistory, 'trace', ''),
      ...(identityVerified ? { identityVerified } : {}),
    },
    sessionId,
    type: 'app',
  });

  return (
    <TranslationScope path="errorPages">
      <FormLayout>
        <Translation path="500">
          {({ content, description, heading, whatYouCanDoSection }) => {
            if (content) {
              return (
                <>
                  {renderMarkdown(
                    content,
                    {},
                    { translationPath: ['errorPages', '500', 'content'] }
                  )}
                  <Translation path="500.cta">
                    {({ href, text }) => (
                      <Button elementType="a" href={href}>
                        {text}
                      </Button>
                    )}
                  </Translation>
                  {__DEBUG__ && (
                    <Section>
                      <p>{title}</p>
                      <pre style={{ color: 'red' }}>
                        {String(error[showStackTrace ? 'stack' : 'message'])}
                      </pre>
                    </Section>
                  )}
                </>
              );
            }

            return (
              <>
                <Section>
                  <span className="error-icon">
                    <IconError width="2.5em" />
                  </span>
                  <Heading level={1}>{heading}</Heading>
                  <Paragraph>{description}</Paragraph>
                  <HorizontalRule />
                </Section>
                <Section>
                  <Optional>
                    <Heading accentBar level={2}>
                      {whatYouCanDoSection.heading}
                    </Heading>
                  </Optional>
                  <Optional>
                    <Translation path="500.whatYouCanDoSection.description">
                      {(description = []) =>
                        reducei18n(description, productCodes).map(
                          ({ content }, i) => (
                            <Paragraph key={`description-${i}`}>
                              {[].concat(content).map(content => {
                                if (content.linkHref) {
                                  return (
                                    <Link
                                      external={content.external}
                                      href={content.linkHref}
                                    >
                                      {content.linkLabel}
                                    </Link>
                                  );
                                } else {
                                  return content;
                                }
                              })}
                            </Paragraph>
                          )
                        )
                      }
                    </Translation>
                  </Optional>
                </Section>
                <Optional>
                  <Translation path="500.returnCta">
                    {({ href, label }) => (
                      <Button elementType="a" href={href}>
                        {label}
                      </Button>
                    )}
                  </Translation>
                </Optional>
                {__DEBUG__ && (
                  <Section>
                    <p>{title}</p>
                    <pre style={{ color: 'red' }}>
                      {String(error[showStackTrace ? 'stack' : 'message'])}
                    </pre>
                  </Section>
                )}
              </>
            );
          }}
        </Translation>
      </FormLayout>
    </TranslationScope>
  );
};

TechnicalError.propTypes = {
  productCodes: T.object,
  title: T.string,
  error: T.object,
  showStackTrace: T.bool,
  sessionId: T.string,
  applicationId: T.string,
  applicationStatus: T.string,
  responseCode: T.string,
  reasonCode: T.string,
  interactionHistory: T.shape({
    currentSegmentName: T.string,
    currentPageName: T.string,
    trace: T.array,
  }),
  identityVerified: T.bool,
};

export default connect((state, ownProps) => ({
  ...ownProps,
  productCodes: state.productCodes,
  sessionId: state.sessionId,
  applicationId: get(state, 'application.applicationId'),
  applicationStatus: get(state, 'application.applicationStatus'),
  responseCode: get(state, 'application.responseCode'),
  reasonCode: get(state, 'application.reasonCode'),
  interactionHistory: state.interactionHistory,
  identityVerified: state.identityVerified,
}))(TechnicalError);
